/**
 * Classifies why a guest trial upload never reached the result page.
 *
 * A pure function on purpose: it is the ONE place that decides what a failed
 * attempt is called, and both destinations — the `trial_upload_failed`
 * analytics event and the Sentry report next to it — are handed the same
 * verdict, so the two channels can never disagree about the same failure.
 *
 * WHY A BUCKET AND NOT THE ERROR: support needs to sort a day of failures into
 * "our API said no" / "the network died" / "something answered that was not our
 * API". A stack trace cannot be grouped that way, and the raw `code` alone
 * cannot either — the errors that matter most (a proxy eating the request, a
 * Cloudflare challenge page) carry no code of ours at all.
 */
import { AnonymousTrialApiError } from './api-client'
import type { AnonymousTrialErrorCode } from './types'

/**
 * The failure buckets, ordered from "the server gave us a verdict" to "we have
 * no idea what answered".
 *
 * `other` is deliberately a bucket rather than a per-code name: it means a code
 * we DO know reached a place we did not expect it to (the smart wrapper is
 * supposed to consume `direct_put_failed` and friends), and the `code`
 * parameter is what tells the reader which one.
 */
export type TrialUploadFailureReason =
  | 'rate_limited'
  | 'trial_disabled'
  | 'upload_stalled'
  | 'upload_not_found'
  | 'network'
  | 'server_error'
  | 'unexpected_response'
  | 'other'

/**
 * The event parameters describing one failed attempt. A `type` and not an
 * `interface` so it keeps the implicit index signature that makes it assignable
 * to `AnalyticsParams`.
 *
 * NO FILENAME, and nothing else the visitor typed or chose — this payload goes
 * to GA4, to Meta and into our own `anonymous_events` table.
 */
export type TrialUploadFailure = {
  reason: TrialUploadFailureReason
  /** HTTP status, omitted when the failure never got one (network, stall). */
  status?: number
  /** Only present when the error was one of ours. */
  code?: AnonymousTrialErrorCode
  /** Which leg of the direct-upload path failed. */
  phase?: TrialUploadPhase
}

/**
 * The submit legs, whitelisted rather than read through.
 *
 * `extras` is the parsed RESPONSE BODY (see `parseErrorBody`), so on the proxy
 * path a `phase` key in it came from whatever answered the request — which in
 * the unexpected_response case is precisely something that is not our API.
 * A GA parameter and a Sentry tag are both searchable dimensions; neither
 * should be free text an intermediary can choose.
 */
type TrialUploadPhase = 'initiate' | 'put' | 'complete'
const UPLOAD_PHASES = new Set<string>(['initiate', 'put', 'complete'])

/** Codes that already have their own `trial_file_rejected` report at the call site. */
const FILE_REJECTION_CODES = new Set<string>(['duration_unreadable', 'file_too_large'])

/** Codes that ARE the answer — the bucket is just the code, renamed for reading. */
const CODE_REASONS = new Map<string, TrialUploadFailureReason>([
  ['rate_limited', 'rate_limited'],
  // Named for what the visitor experienced, not for the server's flag.
  ['anonymous_trial_disabled', 'trial_disabled'],
  ['upload_stalled', 'upload_stalled'],
  ['upload_not_found', 'upload_not_found'],
])

function phaseOf(error: AnonymousTrialApiError): TrialUploadPhase | undefined {
  const phase = error.extras.phase
  return typeof phase === 'string' && UPLOAD_PHASES.has(phase)
    ? (phase as TrialUploadPhase)
    : undefined
}

/**
 * Bucket a thrown value, or `null` when this attempt must NOT be reported as an
 * upload failure.
 *
 * The two `null` cases are the ones that would corrupt the number rather than
 * merely add noise: a visitor pressing cancel is not a failure at all, and a
 * file the server rejected on readability/size already reports itself as
 * `trial_file_rejected` — counting it twice would make the rejection rate and
 * the failure rate share the same people.
 */
export function classifyTrialUploadFailure(cause: unknown): TrialUploadFailure | null {
  // Defensive: the call site checks this first, but the classifier is exported
  // and a cancel recorded as a network failure is a wrong number, not noise.
  if (cause instanceof DOMException && cause.name === 'AbortError') return null

  // Anything that is not one of our errors never got an HTTP answer at all:
  // XHR `onerror` and a failed `fetch` both surface as a bare `TypeError`.
  if (!(cause instanceof AnonymousTrialApiError)) return { reason: 'network' }

  const phase = phaseOf(cause)
  const base = {
    ...(cause.status > 0 ? { status: cause.status } : {}),
    code: cause.code,
    ...(phase ? { phase } : {}),
  }

  const codeReason = CODE_REASONS.get(cause.code)
  if (codeReason) return { reason: codeReason, ...base }
  if (FILE_REJECTION_CODES.has(cause.code)) return null

  if (cause.status === 0) return { reason: 'network', ...base }
  if (cause.status >= 500) return { reason: 'server_error', ...base }
  // `parseErrorBody` falls back to `unknown` exactly when the body would not
  // parse as our JSON, so this IS "something answered that is not our API" —
  // a captive portal, a proxy error page, a Cloudflare challenge. No
  // content-type sniffing needed.
  if (cause.code === 'unknown') return { reason: 'unexpected_response', ...base }
  return { reason: 'other', ...base }
}
